import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { quotes as api, apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, Card, EmptyState, Loading, ConfirmDelete } from "@/components/Shared";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileCheck, Plus, Eye, Pencil, Trash2, Search } from "lucide-react";

export default function Quotes() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.list().then(setList).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { try { await api.remove(id); toast.success("Quote deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!list) return <Loading />;
  const filtered = list.filter((i) => i.number.toLowerCase().includes(q.toLowerCase()) || (i.customer?.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Quotations" subtitle="Send professional quotes and convert them to invoices.">
        <Button onClick={() => nav("/quotes/new")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="new-quote-button"><Plus className="w-4 h-4 mr-1.5" /> New Quote</Button>
      </PageHeader>

      {list.length === 0 ? (
        <EmptyState icon={FileCheck} title="No quotes yet" description="Create a quote to send to your customers."
          action={<Button onClick={() => nav("/quotes/new")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="empty-new-quote"><Plus className="w-4 h-4 mr-1.5" /> New Quote</Button>} />
      ) : (
        <Card>
          <div className="p-4 border-b border-slate-100 relative max-w-sm">
            <Search className="w-4 h-4 text-slate-400 absolute left-7 top-6.5" style={{ top: "1.6rem" }} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search quotes…" className="pl-9" data-testid="quote-search" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-3 font-semibold">Quote</th>
                  <th className="px-5 py-3 font-semibold hidden md:table-cell">Customer</th>
                  <th className="px-5 py-3 font-semibold text-right">Total</th>
                  <th className="px-5 py-3 font-semibold text-center">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((i) => (
                  <tr key={i.id} className="hover:bg-slate-50" data-testid={`quote-row-${i.id}`}>
                    <td className="px-5 py-3.5"><button onClick={() => nav(`/quotes/${i.id}`)} className="font-semibold text-[#0B132B] hover:text-[#D4AF37]">{i.number}</button><div className="text-xs text-slate-400">{fmtDate(i.issue_date)}</div></td>
                    <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">{i.customer?.name || "—"}</td>
                    <td className="px-5 py-3.5 text-right font-semibold text-[#0B132B]">{money(i.total)}</td>
                    <td className="px-5 py-3.5 text-center"><StatusBadge status={i.status} testId={`quote-status-${i.id}`} /></td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="icon" onClick={() => nav(`/quotes/${i.id}`)} data-testid={`view-quote-${i.id}`}><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => nav(`/quotes/${i.id}/edit`)} data-testid={`edit-quote-${i.id}`}><Pencil className="w-4 h-4" /></Button>
                        <ConfirmDelete onConfirm={() => remove(i.id)} title="Delete quote?" trigger={<Button variant="ghost" size="icon" className="text-rose-500" data-testid={`delete-quote-${i.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
